import React, { useEffect, useRef, useState } from 'react';
import { useDrag, useDrop, DropTargetMonitor } from 'react-dnd';
import type { Identifier } from 'dnd-core';
import './columnItem.scss';
import LineMarkerEditModal, { LineAndMarkerSettings } from '../../line-marker-config/LineMarkerEditModal';
import { ReactComponent as EditIcon } from '../../../images/edit-light.svg';
import { useAppDispatch, useAppSelector } from '../../../hooks/storeHooks';
import { resetLineMarker } from '../../line-marker-config/services/LineMarkerService';

interface ColumnItemProps {
  id: string;
  index: number;
  name: string;
  selected: boolean;
  iconUrl: React.ReactNode;
  onSelect: () => void;
  moveColumn: (dragIndex: number, hoverIndex: number) => void;
  handleParameterUpdate: (id: string, updates: Partial<Parameter>, applyNow?: boolean) => void;
  LineType?: string;
  LineColor?: string;
  LineWeight?: number;
  MarkerType?: string;
  MarkerSize?: number;
  MarkerColor?: string;
}

interface DragItem {
  index: number;
  id: string;
  type: string;
}

const ItemType = 'column';

const ColumnItem: React.FC<ColumnItemProps> = ({
  id,
  index,
  name,
  selected,
  iconUrl,
  onSelect,
  moveColumn,
  handleParameterUpdate,
  LineColor,
  LineType,
  LineWeight,
  MarkerType,
  MarkerSize,
  MarkerColor,
}) => {
  const ref = useRef<HTMLLIElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const [{ isDragging }, drag] = useDrag({
    type: ItemType,
    item: { type: ItemType, id: id.toString(), index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsModalOpen(true);
  };

  const [lineMarkerSettings, setLineMarkerSettings] = useState<LineAndMarkerSettings>({
    lineType: LineType as string,
    lineColor: LineColor as string,
    lineWeight: Number(LineWeight),
    markerType: MarkerType as string,
    markerColor: MarkerColor as string,
    markerSize: Number(MarkerSize),
  });

  const handleLineMarkerSettingsChange = (updatedSettings: LineAndMarkerSettings) => {
    setLineMarkerSettings(updatedSettings);

    handleParameterUpdate(id, {
      LineType: updatedSettings.lineType,
      LineColor: updatedSettings.lineColor,
      LineWeight: updatedSettings.lineWeight,
      MarkerType: updatedSettings.markerType,
      MarkerColor: updatedSettings.markerColor,
      MarkerSize: updatedSettings.markerSize,
    });
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (ref.current && !ref.current.contains(event.target as Node)) {
      setIsModalOpen(false);
    }
  };
  useEffect(() => {
    if (isModalOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isModalOpen]);

  useEffect(() => {
    if (textRef.current) {
      const isTextOverflowing = textRef.current.scrollWidth > textRef.current.clientWidth;
      setIsOverflowing(isTextOverflowing);
    }
  }, [name]);

  const [{ handlerId }, drop] = useDrop<DragItem, void, { handlerId: Identifier | null }>({
    accept: ItemType,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item: DragItem, monitor: DropTargetMonitor) {
      if (!ref.current) {
        return;
      }

      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) return;

      const hoverBoundingRect = ref.current?.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      if (!clientOffset || !hoverBoundingRect) {
        return;
      }

      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;

      moveColumn(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  drag(drop(ref));

  const opacity = isDragging ? 0.5 : 1;

  const dispatch = useAppDispatch();
  const preferenceData = useAppSelector((state: any) => state.LineMarkerReset.data?.widgets);
  const timeSeriesData = preferenceData?.find((item: any) => item.key === 'timeseriesChart');
  const defaultLineMarker = timeSeriesData?.widgetProperty?.widgetProperties?.parameterConfig;
  const parameter =
    Array.isArray(defaultLineMarker) && defaultLineMarker[0]?.parameters?.find((param: any) => param.name === name);

  const handleLineReset = () => {
    dispatch(
      resetLineMarker({
        parameterName: name,
        resetType: 'Line',
      }),
    );

    const newSettings = {
      ...lineMarkerSettings,
      lineType: parameter?.lineType || 'solid',
      lineColor: parameter?.lineColor || '#35BB21',
      lineWeight: Number(parameter?.lineWeight) || 1,
    };
    setLineMarkerSettings(newSettings);

    handleParameterUpdate(
      id,
      {
        LineType: newSettings.lineType,
        LineColor: newSettings.lineColor,
        LineWeight: newSettings.lineWeight,
      },
      true,
    );
  };

  const handleMarkerReset = () => {
    dispatch(
      resetLineMarker({
        parameterName: name,
        resetType: 'Marker',
      }),
    );
    const newSettings = {
      ...lineMarkerSettings,
      markerType: parameter?.markerType || 'triangle',
      markerColor: parameter?.markerColor || '#35BB21',
      markerSize: Number(parameter?.markerSize) || 1,
    };
    setLineMarkerSettings(newSettings);

    handleParameterUpdate(
      id,
      {
        MarkerType: newSettings.markerType,
        MarkerColor: newSettings.markerColor,
        MarkerSize: newSettings.markerSize,
      },
      true,
    );
  };

  return (
    <li ref={ref} className='column-item' style={{ opacity }} data-handler-id={handlerId}>
      <div className='column-item__icon-container'>{iconUrl}</div>
      <div className='column-item__content'>
        <div className='column-item__content-container'>
          <div className='column-item__content-inner'>
            <div className='column-item__content-label'>
              <div
                className={`checkbox ${selected ? 'checkbox--selected' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onSelect();
                }}
              >
                {selected && (
                  <svg width='14' height='14' viewBox='0 0 14 14' fill='none' xmlns='http://www.w3.org/2000/svg'>
                    <path
                      d='M11.6666 3.5L5.24992 9.91667L2.33325 7'
                      stroke='white'
                      strokeWidth='2'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                )}
              </div>
              <span className='text' ref={textRef} title={isOverflowing ? name : ''}>{name}</span>
            </div>
          </div>
        </div>
      </div>
      {LineColor && MarkerType && (
        <div className='parameter-actions'>
          <div
            className={`parameter-line ${LineType} ${MarkerType}`}
            style={{ color: LineColor, '--shape-color': MarkerColor } as React.CSSProperties}
          >
            {MarkerType === 'asterick' && (
              <>
                <span className='bar-second' />
                <span className='bar-third' />
              </>
            )}
          </div>
          {/* <div className='parameter-action-icon' /> */}
          <div onClick={handleEditClick}>
            <EditIcon />
          </div>
        </div>
      )}
      {isModalOpen && (
        <LineMarkerEditModal
          initialSettings={lineMarkerSettings}
          onLineMarkerSettingsChange={handleLineMarkerSettingsChange}
          onLineReset={handleLineReset}
          onMarkerReset={handleMarkerReset}
        />
      )}
    </li>
  );
};

export default ColumnItem;
